import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "./Icon";
import SettingsModal from "./SettingsModal";
import { sessionStorageService, AUTH_ROUTES } from "../../features/authentication";
import "../../styles/ai/ai-sidebar.css";

// Hidden for now; flip to true to bring the button back (the /personal-agent route still works).
const SHOW_PERSONAL_AGENT_BUTTON = false;

function getDateLabel(ts) {
    if (!ts) return "Earlier";
    const date  = new Date(ts);
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const d     = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diff  = Math.floor((today - d) / 86400000);

    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";

    return date.toLocaleDateString("en-GB", {
        day:   "2-digit",
        month: "2-digit",
        year:  "numeric",
    });
}

const MENU_WIDTH = 210;
const MENU_HEIGHT = 230;

/**
 * Chat history sidebar for the AI search experience: lists past conversations
 * (pinned first, then grouped by date), a per-chat menu (share, pin, rename,
 * download, delete), chat search, and the account footer.
 */
export default function AiSidebar({
                                      chats = [],
                                      activeChatId = null,
                                      setActiveChatId = () => {},
                                      createNewChat = () => {},
                                      onRenameChat = () => {},
                                      onDownloadChat = null,
                                      onDeleteChat = null,
                                      onPinChat = null,
                                      onShareChat = null,
                                      collapsed = false,
                                      user = null,
                                      historyError = null,
                                      historyLoading = false,
                                      onRetryHistory = () => {},
                                  }) {
    const [search, setSearch] = useState("");
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [editingChatId, setEditingChatId] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    // { chatId, top, left } -- fixed-position so the scrolling chat list can't clip it.
    const [chatMenu, setChatMenu] = useState(null);
    const profileMenuRef = useRef(null);
    const chatMenuRef = useRef(null);
    const editInputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (editingChatId) editInputRef.current?.focus();
    }, [editingChatId]);

    const startRename = (chat) => {
        setEditingChatId(chat.id);
        setEditingValue(chat.title?.trim() || "Untitled Chat");
    };

    const commitRename = () => {
        const trimmed = editingValue.trim();
        if (trimmed) onRenameChat(editingChatId, trimmed);
        setEditingChatId(null);
    };

    const cancelRename = () => setEditingChatId(null);

    const filteredChats = useMemo(() => {
        const term = search.toLowerCase().trim();
        if (!term) return chats;
        return chats.filter((c) => c.title?.toLowerCase().includes(term));
    }, [chats, search]);

    const pinnedChats = useMemo(() => filteredChats.filter((c) => c.pinned), [filteredChats]);

    const groupedChats = useMemo(() => {
        const groups = {};
        filteredChats.filter((c) => !c.pinned).forEach((chat) => {
            const label = getDateLabel(chat.createdAt);
            if (!groups[label]) groups[label] = [];
            groups[label].push(chat);
        });
        return Object.entries(groups);
    }, [filteredChats]);

    const closeChatMenu = useCallback(() => setChatMenu(null), []);

    const openChatMenu = (event, chat) => {
        event.stopPropagation();
        if (chatMenu?.chatId === chat.id) {
            closeChatMenu();
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        const fitsBelow = rect.bottom + MENU_HEIGHT < window.innerHeight - 8;
        setChatMenu({
            chatId: chat.id,
            top: fitsBelow ? rect.bottom + 4 : Math.max(8, rect.top - MENU_HEIGHT - 4),
            left: Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8),
        });
    };

    useEffect(() => {
        if (!profileMenuOpen && !chatMenu) return undefined;

        function handlePointerDown(event) {
            if (profileMenuOpen && profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setProfileMenuOpen(false);
            }
            if (chatMenu && chatMenuRef.current && !chatMenuRef.current.contains(event.target)) {
                setChatMenu(null);
            }
        }
        function handleKey(event) {
            if (event.key === "Escape") {
                setProfileMenuOpen(false);
                setChatMenu(null);
            }
        }

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        document.addEventListener("keydown", handleKey);
        // The list scrolling under a fixed-position menu would detach it from its row.
        const list = document.querySelector(".sidebar-chat-list");
        list?.addEventListener("scroll", closeChatMenu);
        window.addEventListener("resize", closeChatMenu);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
            document.removeEventListener("keydown", handleKey);
            list?.removeEventListener("scroll", closeChatMenu);
            window.removeEventListener("resize", closeChatMenu);
        };
    }, [profileMenuOpen, chatMenu, closeChatMenu]);

    const handleAccountSettings = () => {
        setProfileMenuOpen(false);
        setSettingsOpen(true);
    };

    const handleHelpSupport = () => {
        setProfileMenuOpen(false);
        window.open("mailto:info@oneglobalmarketplace.com?subject=OGM AI Support", "_blank");
    };

    const handleLogout = () => {
        sessionStorageService.remove();
        setProfileMenuOpen(false);
        navigate(AUTH_ROUTES.LOGIN, { replace: true });
    };

    if (collapsed) return null;

    const menuChat = chatMenu ? chats.find((c) => c.id === chatMenu.chatId) : null;
    const runMenuAction = (action) => {
        const chat = menuChat;
        closeChatMenu();
        if (chat) action(chat);
    };
    // Share/pin/download act on the saved row -- a chat with no messages yet has none.
    const savedHint = "Send a message first";

    const renderChat = (chat) => {
        const isActive = chat.id === activeChatId;
        const title = chat.title?.trim() || "Untitled Chat";
        const isEditing = chat.id === editingChatId;
        const menuOpen = chatMenu?.chatId === chat.id;

        return (
            <div
                key={chat.id}
                className={`sidebar-chat-item ${isActive ? "active" : ""} ${menuOpen ? "menu-open" : ""}`}
                onClick={() => !isEditing && setActiveChatId(chat.id)}
            >
                <Icon
                    name={chat.pinned ? "pin" : "chat"}
                    size={14}
                    className={`sidebar-chat-icon ${chat.pinned ? "pinned" : ""}`}
                />

                {isEditing ? (
                    <input
                        ref={editInputRef}
                        className="sidebar-chat-title-input"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") commitRename();
                            if (e.key === "Escape") cancelRename();
                        }}
                        onBlur={commitRename}
                    />
                ) : (
                    <span className="sidebar-chat-title" title={title}>{title}</span>
                )}

                {chat.shareToken && !isEditing && (
                    <span className="sidebar-chat-shared" title="Shared via link">
                        <Icon name="link" size={12} />
                    </span>
                )}

                {!isEditing && (
                    <button
                        type="button"
                        className="sidebar-chat-more"
                        title="More options"
                        aria-label={`More options for ${title}`}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        onClick={(e) => openChatMenu(e, chat)}
                    >
                        <Icon name="moreVertical" size={16} strokeWidth={3} />
                    </button>
                )}
            </div>
        );
    };

    return (
        <aside className="ai-sidebar">
            <div className="sidebar-header">
                <div className="sidebar-brand">
                    <div className="sidebar-brand-icon">
                        <Icon name="sparkle" size={16} />
                    </div>
                    <span className="sidebar-brand-text">OGM AI</span>
                </div>

                <button className="sidebar-new-chat" onClick={createNewChat}>
                    <Icon name="plus" size={15} />
                    New Chat
                </button>

                {/* The site's global header carries the only other "Personal
                    Agent" nav link (App.js), but ai-layout.css deliberately
                    covers that header with a full-viewport z-index:9999
                    overlay on every AI page (see that file's own comment) --
                    so once inside a chat there was no way back to Personal
                    Agent's pause/resume/search-again dashboard at all,
                    short of typing the URL by hand. Hidden on
                    /personal-agent itself since that's already where this
                    would navigate to. */}
                {SHOW_PERSONAL_AGENT_BUTTON && location.pathname !== "/personal-agent" && (
                    <button
                        type="button"
                        className="sidebar-personal-agent-btn"
                        onClick={() => navigate("/personal-agent")}
                    >
                        <Icon name="sparkle" size={15} />
                        Personal Agent
                    </button>
                )}

                <div className="sidebar-search">
                    <Icon name="search" size={14} className="sidebar-search-icon" />
                    <input
                        type="text"
                        placeholder="Search chats…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="sidebar-chat-list">
                {historyError && (
                    <div className="sidebar-history-error" role="alert">
                        <Icon name="alertTriangle" size={14} className="sidebar-history-error-icon" />
                        <span className="sidebar-history-error-text">{historyError}</span>
                        <button
                            type="button"
                            className="sidebar-history-error-retry"
                            onClick={onRetryHistory}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {historyLoading && filteredChats.length === 0 && (
                    <div aria-hidden="true">
                        {[1, 2, 3].map((i) => <div key={i} className="sidebar-skel-item" />)}
                    </div>
                )}

                {!historyLoading && filteredChats.length === 0 && (
                    <div className="sidebar-empty">
                        {search ? "No matches found" : historyError ? "" : "No conversations yet"}
                    </div>
                )}

                {pinnedChats.length > 0 && (
                    <div className="sidebar-date-group">
                        <div className="sidebar-section-label">Pinned</div>
                        {pinnedChats.map(renderChat)}
                    </div>
                )}

                {groupedChats.map(([label, groupChats]) => (
                    <div key={label} className="sidebar-date-group">
                        <div className="sidebar-section-label">{label}</div>
                        {groupChats.map(renderChat)}
                    </div>
                ))}
            </div>

            {/* Portalled: on mobile the sidebar is a transformed slide-in panel, which would make this
                fixed-position menu relative to (and clipped by) the sidebar instead of the screen. */}
            {menuChat && createPortal(
                <div
                    ref={chatMenuRef}
                    className="sidebar-chat-menu"
                    role="menu"
                    style={{ top: chatMenu.top, left: chatMenu.left, width: MENU_WIDTH }}
                >
                    {onShareChat && (
                        <button
                            type="button"
                            role="menuitem"
                            className="sidebar-chat-menu-item"
                            disabled={!menuChat.backendId}
                            title={menuChat.backendId ? undefined : savedHint}
                            onClick={() => runMenuAction(onShareChat)}
                        >
                            <Icon name="share" size={15} />
                            {menuChat.shareToken ? "Manage share link" : "Share conversation"}
                        </button>
                    )}
                    {onPinChat && (
                        <button
                            type="button"
                            role="menuitem"
                            className="sidebar-chat-menu-item"
                            disabled={!menuChat.backendId && !menuChat.messages?.length}
                            title={menuChat.backendId || menuChat.messages?.length ? undefined : savedHint}
                            onClick={() => runMenuAction((c) => onPinChat(c, !c.pinned))}
                        >
                            <Icon name="pin" size={15} />
                            {menuChat.pinned ? "Unpin" : "Pin"}
                        </button>
                    )}
                    <button
                        type="button"
                        role="menuitem"
                        className="sidebar-chat-menu-item"
                        onClick={() => runMenuAction(startRename)}
                    >
                        <Icon name="edit" size={15} />
                        Rename
                    </button>
                    {onDownloadChat && (
                        <button
                            type="button"
                            role="menuitem"
                            className="sidebar-chat-menu-item"
                            disabled={!menuChat.backendId}
                            title={menuChat.backendId ? undefined : savedHint}
                            onClick={() => runMenuAction(onDownloadChat)}
                        >
                            <Icon name="download" size={15} />
                            Download as PDF
                        </button>
                    )}
                    {onDeleteChat && (
                        <>
                            <div className="sidebar-chat-menu-divider" />
                            <button
                                type="button"
                                role="menuitem"
                                className="sidebar-chat-menu-item danger"
                                onClick={() => runMenuAction(onDeleteChat)}
                            >
                                <Icon name="trash" size={15} />
                                Delete
                            </button>
                        </>
                    )}
                </div>,
                document.body,
            )}

            <div className="sidebar-footer" ref={profileMenuRef}>
                <div className="sidebar-footer-row">
                    <button
                        type="button"
                        className="sidebar-footer-trigger"
                        onClick={() => setProfileMenuOpen((v) => !v)}
                        aria-haspopup="menu"
                        aria-expanded={profileMenuOpen}
                        aria-label="Account menu"
                    >
                        {user?.avatar ? (
                            <img
                                src={user.avatar}
                                alt={user.name}
                                className="sidebar-avatar sidebar-avatar-img"
                                referrerPolicy="no-referrer"
                            />
                        ) : (
                            <div className="sidebar-avatar">{user?.initials || "U"}</div>
                        )}
                        <div className="sidebar-user-info">
                            <div className="sidebar-user-name" title={user?.name || "User"}>
                                {user?.name || "User"}
                            </div>
                            <div className="sidebar-user-role">{user?.role || "Member"}</div>
                        </div>
                    </button>
                    <button
                        type="button"
                        className="sidebar-footer-gear"
                        title="Settings"
                        aria-label="Settings"
                        onClick={() => { setProfileMenuOpen(false); setSettingsOpen(true); }}
                    >
                        <Icon name="settings" size={17} />
                    </button>
                </div>

                {profileMenuOpen && (
                    <div className="sidebar-profile-menu" role="menu">
                        <button
                            type="button"
                            role="menuitem"
                            className="sidebar-profile-menu-item"
                            onClick={handleAccountSettings}
                        >
                            <Icon name="settings" size={15} />
                            Account Settings
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className="sidebar-profile-menu-item"
                            onClick={handleHelpSupport}
                        >
                            <Icon name="chat" size={15} />
                            Help &amp; Support
                        </button>
                        <div className="sidebar-profile-menu-divider" />
                        <button
                            type="button"
                            role="menuitem"
                            className="sidebar-profile-menu-item danger"
                            onClick={handleLogout}
                        >
                            <Icon name="logout" size={15} />
                            Logout
                        </button>
                    </div>
                )}
            </div>

            <SettingsModal
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                user={user}
            />
        </aside>
    );
}
